'use strict';

/**
 * Collection for objects
 *
 * @category   Collection
 * @module     collection
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var Collection = require('base/Colido.Collection');
var ObjectModel = require('../models/object');

var MaterialView = require('modules/collection/views/components/filter/material');
var LocalityView = require('modules/collection/views/components/filter/locality');
var SizeView = require('modules/collection/views/components/filter/size');
var EnumView = require('modules/collection/views/components/filter/enum');
var ColorsView = require('modules/collection/views/components/filter/colors');
var CatalogingDateView = require('modules/collection/views/components/filter/cataloging-date');
var ObtainingDateView = require('modules/collection/views/components/filter/obtaining-date');
var FindingDateView = require('modules/collection/views/components/filter/finding-date');
var RangeSliderView = require('modules/collection/views/components/filter/range-slider');
var HistoryView = require('modules/collection/views/components/filter/history');
var PreviousOwnersView = require('modules/collection/views/components/filter/previous-owners');

var ObjectsCollection = Collection.extend({
    api: function() {
        var api = '/objects';

        if (this.catalog) {
            api =  this.catalog.api + '/' + this.catalog.id + '/objects';
        }

        return api;
    },

    getName: function() {
        var name = 'collection:objects';

        if (this.catalog) {
            name =  this.catalog.collection.name + ':' + this.catalog.id + ':objects';
        }

        return name;
    },

    name: 'collection:objects',

    model: ObjectModel,

    sortDefault: 'modifiedTime.desc',

    // sortFieldsMap: {
    //     'name': 'properName',
    //     'locality.asc': 'locality.asc-name.asc',
    //     'locality.desc': 'locality.desc-name.desc',
    //     'dimensions': 'volume',
    //     'dimensionsType': 'dimensionsTypeAsNumber',
    //     'weight': 'normalizedWeight',
    //     'lastEdited': 'modifiedTime',
    //     'location': 'storage',
    // },

    searchFields: [
        'colorSpecification',
        'damages',
        'description',
        'dimensionType',
        'estimatedValue',
        'estimatedValueCurrency',
        'id',
        'insuranceValue',
        'insuranceValueCurrency',
        'locality',
        'maxCrystalSize',
        'modifiedTime',
        'notes',
        'objectHistory',
        'objectId',
        'objectStatus',
        'objectStatusRelation',
        'obtainedDate',
        'obtainedFrom',
        'previousOwners',
        'properName',
        'retailPrice',
        'retailPriceCurrency',
        'sizeDepth',
        'sizeHeight',
        'sizeUnit',
        'sizeWidth',
        'status',
        'storage',
        'weight',
        'weightUnit',
    ],

    //@todo do we need this?
    // filterFieldsMap: {
    //     'weight': 'weightConverted',
    // },

    catalog: null,

    initialize: function(models, options) {
        options = options || {};

        if (!options.catalog) {
            return Colido.config.debug && console.log('You have to provide a catalog model as option when initializing an objects collection');
        }

        this.catalog = options.catalog;

        this.name = this.getName();

        this.sortDefault = this.catalog.get('fieldsDefaultSort');

        this.applyPatchExtension();

        this.on('destroy', function(object) {
            App.radio('collection').trigger('object:destroy', object);
        });

        this.listenTo(App.radio('collection').vent, 'location:destroy', this.removeStorageIds.bind(this));

        this.listenTo(App.radio(this.name).vent, 'updated', function(data) {
            this.setFromWebsocket(data, {remove: false});
        }.bind(this));

        this.listenTo(App.radio(this.name).vent, 'removed', function(data) {
            this.remove(data); //data [1,2,3]
        }.bind(this));

        this.listenTo(App.radio(this.name).vent, 'added', function(data) {
            this.add(data);
        }.bind(this));
    },

    /**
     * Override default comparator to order by id, when default comparison is equal
     * @param {Backbone.Model} a Model a
     * @param {Backbone.Model} b Model b
     * @returns {integer}
     */
    comparator: function(a, b) {
        var compare = Collection.prototype.comparator.call(this, a, b);

        if (compare === 0) {
            return parseInt(a.get('id')) < parseInt(b.get('id')) ? -1 : 1;
        } else {
            return compare;
        }
    },

    removeStorageIds: function(storageModel, storagesCollection) {
        this.where({storageId: storageModel.id}).forEach(function(object){
            object.set({storageId: null});
        })
    },

    /**
     * Returns a collection where no storage is set
     * @returns filteredCollection
     */
    getObjectsWithNoStorageCollection: function() {
        var filteredCollection = this.getFilteredCollection(function(model) {
            return model.get('storageId') === null;
        });

        if (this.catalog) {
            filteredCollection.catalog = this.catalog;
        }

        return filteredCollection;
    },

    _getFilterRule: function(model, criteria) {
        var multipleValues;

        var field = this.catalog.getFilterField(criteria.filterKey);

        if (field && field.filter) {
            var filterAttribute = field.filter.modelAttribute || field.name;

            // switch(criteria.filterKey) {
            switch(field.filter.type) {
                case 'range-slider':
                    return this.isValueInRange(model.getNormalizedValue(filterAttribute), criteria.requestedValue);

                case 'bool':
                    var modelValue = model.get(filterAttribute);
                    modelValue = modelValue + ''; //cast to string for comparison

                    return modelValue + '' === criteria.requestedValue;

                //multiple values OR
                case 'color':
                    var colors = model.get(filterAttribute);
                    multipleValues = colors && colors.length ? colors : 'undefined';

                    return this.isValueInList(multipleValues, criteria.requestedValue);

                case 'species':
                    multipleValues = _.pluck(model.get(filterAttribute), 'id');

                    return this.isValueInList(multipleValues, criteria.requestedValue);

                case 'history':
                    var objectHistory = model.get(filterAttribute);
                    multipleValues = objectHistory.length ? _.last(_.pluck(objectHistory, 'type')) : 'undefined';

                    return this.isValueInList(multipleValues, criteria.requestedValue);

                case 'enum':
                    multipleValues = model.get(filterAttribute);
                    multipleValues = multipleValues && multipleValues.length ? multipleValues : 'undefined';

                    return this.isValueInList(multipleValues, criteria.requestedValue);

                case 'dimensionType':
                    multipleValues = model.get(filterAttribute);

                    return this.isValueInList(multipleValues, criteria.requestedValue);

                case 'locality':
                    var locality = model.get(filterAttribute);
                    var localityParts = locality ? _.values(locality.parts) : [];

                    multipleValues = localityParts.length ? localityParts : 'undefined';

                    return this.isValueInList(multipleValues, criteria.requestedValue);

                //@todo
                case 'previousOwner':
                    var objectHistory = model.get('objectHistory');
                    multipleValues = objectHistory.length ? _.last(_.pluck(objectHistory, 'from')) : 'undefined';

                    return this.isValueInList(multipleValues, criteria.requestedValue);

                //multiple values AND
                // @todo probably not needed

                //nested values
                // case 'sizeUnit':
                //     return model.get('size')[3] === criteria.requestedValue;

                //special cases
                case 'findingDate':
                    var matchesFilter = false;
                    var requestedValues = criteria.requestedValue.split(',');
                    var modelValue = model.get('findingDate');
                    requestedValues.forEach(function(requestedValue){
                        if (requestedValue.indexOf('~') > -1) {
                            var range = requestedValue.split('~');
                            matchesFilter = matchesFilter || modelValue >= range[0] && modelValue <= range[1];
                        } else {
                            matchesFilter = matchesFilter || requestedValue === 'none' && modelValue == null;
                        }
                    });

                    return matchesFilter;
            }
        }

        return Collection.prototype._getFilterRule.apply(this, arguments);
    },

    getObjectIdsByScannerTags: function(tags) {
        tags = _.isArray(tags) ? tags : [tags];

        var matchingObjects = _.unique(this.filter(function(objectModel){
            return _.contains(tags, objectModel.get('rfidTag')); //@todo this is a hard coded field name and should be avoided
        }));

        return  _.pluck(matchingObjects, 'id');
    },

    //for filters
    componentViews: {
        'species': MaterialView,
        'locality': LocalityView,
        'dimensionType': SizeView,
        'enum': EnumView,
        'colors': ColorsView,
        'cataloging-date': CatalogingDateView,
        'obtaining-date': ObtainingDateView,
        'finding-date': FindingDateView,
        'range-slider': RangeSliderView,
        'history': HistoryView,
        'previous-owners': PreviousOwnersView,
    },
});

module.exports = ObjectsCollection;